/**
 * State hashing for desync detection and replay integrity.
 *
 * The simulation (or any other producer) exposes a plain-data snapshot of the state that matters for
 * determinism; a `StateHasher` turns it into a short string. The default hasher serializes the snapshot as
 * canonical JSON (sorted keys, explicit non-finite numbers) and hashes it with a fast 64-bit non-cryptographic
 * hash. Client and server must use the same algorithm; the algorithm id travels with checkpoints.
 */

export interface StateHasher {
  /** Algorithm identifier, e.g. `cjson-h64`. */
  readonly algorithm: string;
  hash(state: unknown): string;
}

/**
 * 64-bit string hash (two 32-bit lanes, cyrb53-style mixing, full 64-bit output as 16 hex chars).
 * Deterministic across JS engines: uses only `Math.imul`, xor and shifts on UTF-16 code units.
 */
export function hash64Hex(text: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

function numberToken(n: number): string {
  if (Number.isNaN(n)) return '"#NaN"';
  if (n === Infinity) return '"#Inf"';
  if (n === -Infinity) return '"#-Inf"';
  if (Object.is(n, -0)) return '0';
  return JSON.stringify(n);
}

/**
 * Canonical JSON: object keys sorted, `undefined`/function members skipped, non-finite numbers encoded as
 * tagged strings, typed arrays as arrays, Maps as sorted `[key, value]` pairs, Sets as sorted arrays.
 * Throws on cycles.
 */
export function canonicalJson(value: unknown): string {
  const stack: object[] = [];
  const walk = (v: unknown): string | undefined => {
    if (v === null) return 'null';
    switch (typeof v) {
      case 'number':
        return numberToken(v);
      case 'string':
        return JSON.stringify(v);
      case 'boolean':
        return v ? 'true' : 'false';
      case 'bigint':
        return JSON.stringify('#big:' + v.toString());
      case 'undefined':
      case 'function':
      case 'symbol':
        return undefined;
    }
    const obj = v as object;
    if (stack.includes(obj)) throw new Error('canonicalJson: cyclic structure');
    stack.push(obj);
    try {
      if (ArrayBuffer.isView(obj) && !(obj instanceof DataView)) {
        return '[' + Array.from(obj as unknown as ArrayLike<number>, (n) => numberToken(Number(n))).join(',') + ']';
      }
      if (Array.isArray(obj)) {
        return '[' + obj.map((x) => walk(x) ?? 'null').join(',') + ']';
      }
      if (obj instanceof Map) {
        const pairs = [...obj.entries()].map(([k, x]) => [walk(k) ?? 'null', walk(x) ?? 'null'] as const);
        pairs.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
        return '{"#map":[' + pairs.map(([k, x]) => '[' + k + ',' + x + ']').join(',') + ']}';
      }
      if (obj instanceof Set) {
        const items = [...obj].map((x) => walk(x) ?? 'null').sort();
        return '{"#set":[' + items.join(',') + ']}';
      }
      const rec = obj as Record<string, unknown>;
      const keys = Object.keys(rec).sort();
      const parts: string[] = [];
      for (const k of keys) {
        const s = walk(rec[k]);
        if (s !== undefined) parts.push(JSON.stringify(k) + ':' + s);
      }
      return '{' + parts.join(',') + '}';
    } finally {
      stack.pop();
    }
  };
  return walk(value) ?? 'null';
}

/** Default hasher: canonical JSON + hash64Hex. */
export class CanonicalJsonHasher implements StateHasher {
  readonly algorithm = 'cjson-h64';
  hash(state: unknown): string {
    return hash64Hex(canonicalJson(state));
  }
}

export const defaultStateHasher: StateHasher = new CanonicalJsonHasher();
